import React, { useState } from 'react';
import { PackageProduct } from '@ajat/design-system/ShoppingCart';
import { ComponentPage, DemoCard } from '../../shared';
import { PACKAGE_IMG, INCLUDED_PRODUCTS } from './cartDemoData';

export const meta = {
  component: PackageProduct,
  componentName: 'PackageProduct',
  title: 'Package Product',
  group: 'Shopping Cart',
  importPath: '@ajat/design-system/ShoppingCart',
  description:
    'A package line item — the same shape as Simple Product plus a savings badge and an expandable list of the products it contains. Included rows accept a subtitle array, which is how per-attribute lines ("Navnetryk: Ja") are rendered under the colour and size.',
  props: [
    ['image', 'string', '—', 'Package image'],
    ['imageAlt', 'string', "''", 'Alt text'],
    ['title', 'string', '—', 'Package name'],
    ['price', 'string', '—', 'Fixed price string — ignored when unitPrice is set'],
    ['unitPrice', 'number', '—', 'Price per unit; the row shows unitPrice × quantity'],
    ['priceState', "'default' | 'discounted' | 'included'", "'default'", 'Price state passed to Price'],
    ['originalPrice', 'string', "''", 'Fixed struck-through price'],
    ['unitOriginalPrice', 'number', '—', 'Struck-through price per unit; scales with quantity'],
    ['savingsText', 'string', "''", 'Savings badge, e.g. "SPARA 200:-"'],
    ['priceSuffix', 'string', "',-'", 'Suffix for computed prices'],
    ['quantity', 'number', '—', 'Controlled quantity'],
    ['onQuantityChange', 'func', '—', 'Fires with the new quantity'],
    ['lockQuantity', 'bool', 'false', 'Disables the stepper — price-locked paid orders'],
    ['onEdit', 'func', '—', 'Edit handler. Omit it and the button is hidden.'],
    ['onRemove', 'func', '—', 'Remove handler. Omit it and the button is hidden.'],
    ['includedProducts', 'array', '[]', '[{ image, title, subtitle }] — subtitle may be an array of lines'],
    ['includedTitle', 'string', "'INCLUDED PRODUCTS'", 'Heading above the included list'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  propsNote:
    'This component declares no propTypes, so the props-drift test cannot verify this table — keep it in step with the component by hand. Bonus products the backend appends are filtered out of the minicart count but render here like any other included row.',
  usage: [
    {
      title: 'A package row',
      code: `import { PackageProduct } from '@ajat/design-system/ShoppingCart';

<PackageProduct
  image={pkg.image}
  title={pkg.name}
  unitPrice={pkg.unitPrice}
  unitOriginalPrice={pkg.unitOriginalPrice}
  priceState="discounted"
  savingsText="Spar 20%"
  quantity={pkg.quantity}
  onQuantityChange={(qty) => updatePackageQty(pkg.id, qty)}
  includedProducts={pkg.includedProducts.map((p) => ({
    image: p.image,
    title: p.productName,
    // An array renders one line each — colour/size, then attributes
    subtitle: [
      [p.color, p.size].filter(Boolean).join(', '),
      ...(p.attributesList || []).map((a) => \`\${a.label}: \${a.value}\`),
    ],
  }))}
  onEdit={() => reopenPackage(pkg)}
  onRemove={() => removePackage(pkg.id)}
/>`,
    },
  ],
};

export default function PackageProductPage() {
  const [qty1, setQty1] = useState(1);
  const [qty2, setQty2] = useState(1);

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Discounted package" hint="unitPrice × quantity">
        <PackageProduct
          image={PACKAGE_IMG}
          imageAlt="Royal Pakke 4"
          title="Royal Pakke 4"
          unitPrice={499}
          priceState="discounted"
          unitOriginalPrice={699}
          savingsText="SPARA 200:-"
          quantity={qty1}
          onQuantityChange={setQty1}
          includedProducts={INCLUDED_PRODUCTS}
          onEdit={() => {}}
          onRemove={() => {}}
        />
      </DemoCard>

      <DemoCard title="Default price">
        <PackageProduct
          image={PACKAGE_IMG}
          imageAlt="Royal Pakke 3"
          title="Royal Pakke 3"
          unitPrice={399}
          quantity={qty2}
          onQuantityChange={setQty2}
          includedProducts={INCLUDED_PRODUCTS.slice(0, 3)}
          onEdit={() => {}}
          onRemove={() => {}}
        />
      </DemoCard>

      <DemoCard title="Multi-line subtitles" hint="attribute lines under colour and size">
        <PackageProduct
          image={PACKAGE_IMG}
          title="Royal Pakke 4"
          unitPrice={499}
          quantity={1}
          includedProducts={[
            {
              ...INCLUDED_PRODUCTS[0],
              subtitle: ['Red, S', 'Navnetryk: Archie', 'Lille brysttryk: Ja'],
            },
            { ...INCLUDED_PRODUCTS[1], subtitle: ['Red, S', 'Navnetryk: Archie'] },
            { ...INCLUDED_PRODUCTS[3], subtitle: 'Bonus' },
          ]}
          onRemove={() => {}}
        />
      </DemoCard>

      <DemoCard title="Locked quantity" hint="price-locked paid order">
        <PackageProduct
          image={PACKAGE_IMG}
          title="Royal Pakke 4"
          unitPrice={499}
          quantity={2}
          lockQuantity
          includedProducts={INCLUDED_PRODUCTS.slice(0, 2)}
          onRemove={() => {}}
        />
      </DemoCard>
    </ComponentPage>
  );
}
